import React, { FC, useCallback, useEffect, useState } from 'react';
import { getMetricsProperty } from 'app/modules/insights/modules/pagePerformance/utils/metricProperty';
import { OrderedMetricCard } from 'app/modules/insights/modules/pagePerformance/types/graphs';
import PageMetricCard from 'app/modules/insights/modules/pagePerformance/components/pageMetricCard/PageMetricCard';
import { Page } from 'app/modules/insights/types/Page';
import { useUpdateMetricsUserConfiguration } from 'app/modules/insights/modules/pagePerformance/hooks/useUpdateMetricsUserConfiguration';
import PageTypeUtils from 'app/utils/pageType';
import { useWindowSize } from 'utils/hooks/useWindowsSize';
import clsx from 'clsx';
import { PageNumberType } from '@kontentino/kontentino-constants/Pages';
import { Divider } from '@kontentino/ui';
import { useUser } from 'modules/user/userSelector';
import { ANALYTICS_LITE_PAGE_PERFORMANCE } from '../utils/pagePerformanceMetrics';

const CARD_WIDTH_LARGE = 528;
const ONE_THIRD = '32.3%';
const LARGE_DESKTOP_VIEWPORT = 1728;

type Props = {
  page: Page;
};

const InsightLitePagePerformance: FC<Props> = ({ page }) => {
  const { width: windowWidth } = useWindowSize();
  const { useMeridianTime } = useUser();

  const { updateMetricsUserConfiguration } =
    useUpdateMetricsUserConfiguration(page);

  const initDefaultMetrics = useCallback(() => {
    const platformMetrics = ANALYTICS_LITE_PAGE_PERFORMANCE[page._type] || [];
    return platformMetrics.map((metric) => ({
      metricKey: metric.key,
      expanded: false,
    }));
  }, [page._type]);

  const [metrics, setMetrics] =
    useState<OrderedMetricCard[]>(initDefaultMetrics);

  useEffect(() => {
    setMetrics(initDefaultMetrics());
  }, [page.id, initDefaultMetrics]);

  const toggleExpandedMetric = (metricKey: string) => {
    const newMetrics = metrics.map((metric) =>
      metric.metricKey === metricKey
        ? { metricKey: metric.metricKey, expanded: !metric.expanded }
        : metric,
    );

    updateMetricsUserConfiguration(newMetrics);
    setMetrics(newMetrics);
  };

  const commonDataNamePrefix = `insights-lite_page-performance_${PageTypeUtils.getName(
    page._type as PageNumberType,
  )}`;

  const calculateCardWidth = (isExpanded: boolean): number | string => {
    if (isExpanded) return '100%';
    return windowWidth && windowWidth >= LARGE_DESKTOP_VIEWPORT
      ? CARD_WIDTH_LARGE
      : ONE_THIRD;
  };

  const metricsCards = metrics
    .map((metric) => ({
      metric,
      metricProperty: getMetricsProperty(page._type, metric.metricKey),
    }))
    .filter(({ metricProperty }) => !!metricProperty);

  return (
    <div className="tw-mt-2 tw-w-full">
      <div className="tw-mb-6 tw-w-full">
        <div
          data-name={`${commonDataNamePrefix}_metric-cards-wrapper`}
          className="tw-flex tw-w-full tw-flex-wrap tw-gap-4"
        >
          {metricsCards.map(({ metricProperty, metric }) => (
            <div
              key={metric.metricKey}
              className={clsx('tw-flex', {
                'tw-w-full': metric.expanded,
              })}
              style={{
                width: calculateCardWidth(metric.expanded),
              }}
            >
              <PageMetricCard
                useMeridianTime={useMeridianTime}
                page={page}
                metric={metricProperty}
                expanded={metric.expanded}
                onToggleExpandClick={() =>
                  toggleExpandedMetric(metric.metricKey)
                }
                dataName={{
                  card: `${commonDataNamePrefix}-single-metric_card`,
                  expandButton: `${commonDataNamePrefix}-${metric.metricKey}_${
                    metric.expanded ? 'collapse' : 'expand'
                  }`,
                  summaryMetrics: `${commonDataNamePrefix}-${metric.metricKey}_metrics-summary-wrapper`,
                  metricTable: `${commonDataNamePrefix}-${metric.metricKey}_metric-table`,
                  submetricsTabBar: `${commonDataNamePrefix}-${metric.metricKey}_submetrics-tabs`,
                }}
              />
            </div>
          ))}
        </div>
      </div>
      <Divider className="tw-my-6" />
    </div>
  );
};

export default InsightLitePagePerformance;
